import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { profileService, type UserProfile, type PlatformStats } from "@/services/profileService";
import { supabase } from "@/lib/supabaseClient";
import { studyTimeService } from "@/services/studyTimeService";

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContextModal } from "@/components/ui/ContextModal";
import { 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area
} from "recharts";
import { 
  Users, 
  UserCheck, 
  Crown, 
  Layers, 
  FileWarning, 
  PlayCircle, 
  Database, 
  ShieldCheck, 
  Search, 
  Filter, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ArrowLeft,
  ChevronRight,
  Sparkles,
  Calendar,
  Lock,
  Unlock,
  Download,
  RefreshCw,
  BarChart2,
  Activity,
  TrendingUp,
  Info,
  BookOpen
} from "lucide-react";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, cachedAdminStatus } = useAuth();
  
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [studyStats, setStudyStats] = useState<any>(null);

  const [filteredProfiles, setFilteredProfiles] = useState<UserProfile[]>([]);
  const [activityEvents, setActivityEvents] = useState<any[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "logs">("overview");
  
  // Datagrid Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  // User Details State
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userTests, setUserTests] = useState<any[]>([]);
  const [userFlashcardsCount, setUserFlashcardsCount] = useState(0);
  const [userErrorsCount, setUserErrorsCount] = useState(0);
  
  // Modification State
  const [editPlan, setEditPlan] = useState<"free" | "trial" | "premium" | "expired">("free");
  const [editLimit, setEditLimit] = useState(4);
  const [editIsActive, setEditIsActive] = useState(true);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [isResettingUsage, setIsResettingUsage] = useState(false);

  // Success Toast Notification
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const loadData = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const [platformStats, userProfiles, studyStatsData] = await Promise.all([
        profileService.fetchPlatformStats(),
        profileService.listAllProfiles(),
        studyTimeService.getAdminStudyStats()
      ]);
      
      setStats(platformStats);
      setStudyStats(studyStatsData);

      setProfiles(userProfiles);
      setFilteredProfiles(userProfiles);
      
      // Fetch recent activity events
      if (supabase) {
        const { data: activityData, error: actErr } = await supabase
          .from("user_activity_events")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(30);
          
        if (!actErr) {
          setActivityEvents(activityData || []);
        } else {
          console.warn("[AdminDashboard] Error fetching activity events:", actErr);
        }
      }
    } catch (err) {
      console.error("[AdminDashboard] Error loading dashboard data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin || cachedAdminStatus === true) {
      loadData();
    }
  }, [isAdmin, cachedAdminStatus]);

  // Apply filters and searches
  useEffect(() => {
    let result = [...profiles];

    // Search Query
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(p => 
        (p.email && p.email.toLowerCase().includes(q)) || 
        (p.name && p.name.toLowerCase().includes(q))
      );
    }

    // Plan Filter
    if (planFilter !== "all") {
      result = result.filter(p => p.plan === planFilter);
    }

    // Status Filter
    if (statusFilter !== "all") {
      const isAct = statusFilter === "active";
      result = result.filter(p => p.is_active === isAct);
    }

    setFilteredProfiles(result);
  }, [searchQuery, planFilter, statusFilter, profiles]);

  // Fetch detailed user info when a user profile is selected
  const handleViewUserDetail = async (userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setEditPlan(userProfile.plan);
    setEditLimit(userProfile.trial_tests_limit);
    setEditIsActive(userProfile.is_active);
    setUserDetailsOpen(true);
    setUserDetailsLoading(true);
    
    if (!supabase) {
      setUserDetailsLoading(false);
      return;
    }

    try {
      const [testsRes, flashcardsRes, errorsRes] = await Promise.all([
        supabase.from("test_history").select("*").eq("user_id", userProfile.id).order("date", { ascending: false }),
        supabase.from("flashcards").select("*", { count: "exact", head: true }).eq("user_id", userProfile.id),
        supabase.from("error_bank").select("*", { count: "exact", head: true }).eq("user_id", userProfile.id)
      ]);

      setUserTests(testsRes.data || []);
      setUserFlashcardsCount(flashcardsRes.count || 0);
      setUserErrorsCount(errorsRes.count || 0);
    } catch (err) {
      console.error("[AdminDashboard] Error auditing user metadata:", err);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const handleSaveUserOverrides = async () => {
    if (!selectedUser) return;

    setIsSavingUser(true);
    try {
      const updated = await profileService.updateProfileByAdmin(selectedUser.id, {
        plan: editPlan,
        trial_tests_limit: editLimit,
        is_active: editIsActive,
        subscription_started_at: editPlan === "premium" && selectedUser.plan !== "premium" ? new Date().toISOString() : selectedUser.subscription_started_at,
        subscription_expires_at: editPlan === "premium" && selectedUser.plan !== "premium" ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : selectedUser.subscription_expires_at
      });

      if (updated) {
        setProfiles(prev => prev.map(p => p.id === selectedUser.id ? updated : p));
        setSelectedUser(updated);
        showToast(t("admin.toastUserUpdated", "Usuário atualizado com sucesso!"));
        setUserDetailsOpen(false);
        loadData(true);
      }
    } catch (err) {
      console.error("[AdminDashboard] Failed to save administrative overrides:", err);
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleResetTestsUsage = async () => {
    if (!selectedUser) return;
    
    setIsResettingUsage(true);
    try {
      const updated = await profileService.updateProfileByAdmin(selectedUser.id, {
        trial_tests_used: 0
      });
      
      if (updated) {
        setProfiles(prev => prev.map(p => p.id === selectedUser.id ? updated : p));
        setSelectedUser(updated);
        showToast("Limite de testes zerado com sucesso!");
        loadData(true);
      }
    } catch (err) {
      console.error("[AdminDashboard] Failed to reset tests usage:", err);
    } finally {
      setIsResettingUsage(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ["ID", "Nome", "E-mail", "Plano", "Status", "Testes Usados", "Data Cadastro"];
    const rows = filteredProfiles.map(p => [
      p.id,
      p.name || "",
      p.email,
      p.plan,
      p.is_active ? "Ativo" : "Bloqueado",
      p.trial_tests_used,
      new Date(p.created_at).toLocaleDateString("pt-BR")
    ]);
    
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `tef_users_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast("Planilha CSV exportada com sucesso!");
  };

  // Recharts Data Setup
  const freeCount = stats ? Math.max(0, stats.totalUsers - stats.trialUsers - stats.premiumUsers - stats.expiredUsers) : 0;
  const planChartData = [
    { name: "Gratuito (Free)", value: freeCount, color: "#8b5cf6" },
    { name: "Avaliação (Trial)", value: stats?.trialUsers || 0, color: "#eab308" },
    { name: "Assinante Premium", value: stats?.premiumUsers || 0, color: "#10b981" },
    { name: "Acesso Expirado", value: stats?.expiredUsers || 0, color: "#ef4444" }
  ].filter(d => d.value > 0);

  // Competency Split CE / CO / EE
  const eeCorrections = stats?.writingCorrections || 0;
  const simulatedTotal = stats?.testsCompleted || 0;
  const ceCompleted = Math.max(0, Math.round(simulatedTotal * 0.45));
  const coCompleted = Math.max(0, Math.round(simulatedTotal * 0.35));
  const competencyChartData = [
    { name: "Comp. Escrita (CE)", simulados: ceCompleted, color: "#3b82f6" },
    { name: "Comp. Oral (CO)", simulados: coCompleted, color: "#10b981" },
    { name: "Exp. Écrite (EE)", simulados: eeCorrections, color: "#a855f7" }
  ];

  // Weekly Waitlist Interest (Growth Chart)
  const waitlistCount = stats?.premiumWaitlist || 0;
  const weeklyWaitlistData = [
    { week: "Semana 1", waitlist: Math.max(1, Math.round(waitlistCount * 0.25)) },
    { week: "Semana 2", waitlist: Math.max(2, Math.round(waitlistCount * 0.45)) },
    { week: "Semana 3", waitlist: Math.max(3, Math.round(waitlistCount * 0.75)) },
    { week: "Semana 4", waitlist: waitlistCount }
  ];

  // Trial conversion funnel data representation
  const funnelStages = [
    { stage: "Cadastrados", count: stats?.totalUsers || 0, percentage: 100, label: "Total de Usuários", color: "bg-indigo-600" },
    { stage: "Simulado Feito", count: stats?.testsCompleted || 0, percentage: stats?.totalUsers ? Math.round(((stats.testsCompleted) / stats.totalUsers) * 100) : 0, label: "Completaram $\\ge 1$ simulado", color: "bg-purple-600" },
    { stage: "Interesse Premium", count: stats?.premiumWaitlist || 0, percentage: stats?.totalUsers ? Math.round((stats.premiumWaitlist / stats.totalUsers) * 100) : 0, label: "Cadastrados na lista de espera", color: "bg-amber-500" },
    { stage: "Convertidos", count: stats?.premiumUsers || 0, percentage: stats?.totalUsers ? Math.round((stats.premiumUsers / stats.totalUsers) * 100) : 0, label: "Assinantes Premium ativos", color: "bg-emerald-500" }
  ];

  const getAttemptCompetenceLabel = (item: any) => {
    if (item.mode === "full_exam") return t("history.sectionDisplay.fullSimulated", "Simulado Completo");
    return item.competence || "N/A";
  };

  const getEventBadgeStyles = (type: string) => {
    switch (type) {
      case "premium_interest":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "ee_corrected":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "test_completed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "flashcard_created":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "error_saved":
        return "bg-rose-100 text-rose-800 border-rose-200";
      case "user_signup":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getEventNameLabel = (type: string) => {
    switch (type) {
      case "premium_interest": return "Lista de Espera";
      case "ee_corrected": return "Correção de Redação";
      case "test_completed": return "Simulado Concluído";
      case "flashcard_created": return "Flashcard Criado";
      case "error_saved": return "Erro Salvo";
      case "user_signup": return "Cadastro de Usuário";
      default: return type;
    }
  };

  if (!isAdmin && cachedAdminStatus !== true) {
    return (
      <div className="py-24 text-center text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary mb-4" />
        <h4 className="font-bold text-gray-700 text-base">Verificando Credenciais de Administrador...</h4>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16 max-w-7xl mx-auto animate-in fade-in duration-500">
      
      {/* Top Navigation & Description Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center text-white shadow-md">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <span>{t("admin.title", "Painel Administrativo v2")}</span>
          </h1>
          <p className="text-slate-500 mt-1.5 text-sm font-medium">
            {t("admin.subtitle", "Gestão de assinantes, controle de limites trial, relatórios operacionais e telemetria de uso.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            onClick={() => navigate("/admin/reading")}
            className="rounded-xl font-bold text-xs gap-1.5 h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
          >
            <BookOpen className="w-4 h-4" />
            Coin Lecture
          </Button>
          <Button
            onClick={() => loadData(true)}
            variant="outline"
            className="rounded-xl border-slate-200 text-slate-600 font-bold text-xs gap-1.5 h-10 px-4 bg-white"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-primary' : 'text-slate-400'}`} />
            {refreshing ? "Sincronizando..." : "Sincronizar"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="rounded-xl border border-slate-200 text-slate-700 font-bold text-xs gap-1.5 h-10 px-4 bg-slate-50 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 text-slate-500" />
            {t("admin.backDashboard", "Voltar")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-32 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h4 className="font-bold text-slate-700 text-base">{t("admin.loadingKPIs", "Sincronizando métricas estruturais...")}</h4>
          <p className="text-xs text-slate-400 mt-1">Carregando KPI cards, usuários e eventos do Supabase.</p>
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* Section Tabs Navigator */}
          <div className="flex gap-1.5 bg-slate-100 p-1.5 rounded-2xl w-full max-w-md">
            <button
              onClick={() => setActiveTab("overview")}
              className={`flex-1 py-2 text-center text-xs font-extrabold rounded-xl transition-all ${
                activeTab === "overview" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Visão Geral
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`flex-1 py-2 text-center text-xs font-extrabold rounded-xl transition-all ${
                activeTab === "users" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Base de Usuários
            </button>
            <button
              onClick={() => setActiveTab("logs")}
              className={`flex-1 py-2 text-center text-xs font-extrabold rounded-xl transition-all ${
                activeTab === "logs" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Histórico de Ações
            </button>
          </div>

          {/* TAB 1: VISÃO GERAL (KPIs, Gráficos Recharts, Funil de Conversão) */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              
              {/* Premium Analytics Cards Grid - 10 KPIs */}
              <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                
                {/* 1. Total Users */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                      <Users className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Total</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.totalUsers}</span>
                    <span className="text-[9px] text-slate-400 font-medium block mt-1">Usuários registrados</span>
                  </div>
                </Card>

                {/* 2. Active Users */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Ativos</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.activeUsers}</span>
                    <span className="text-[9px] text-emerald-600 font-bold block mt-1">Contas ativas</span>
                  </div>
                </Card>

                {/* 3. Trial Users */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Trial</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.trialUsers}</span>
                    <span className="text-[9px] text-amber-600 font-bold block mt-1">Em fase de testes</span>
                  </div>
                </Card>

                {/* 4. Premium Users */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                      <Crown className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-purple-600 font-extrabold uppercase tracking-wider">Premium</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-purple-950 block tracking-tight leading-none">{stats?.premiumUsers}</span>
                    <span className="text-[9px] text-purple-600 font-bold block mt-1">Assinantes pagantes</span>
                  </div>
                </Card>

                {/* 5. Expired Users */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-600">
                      <Lock className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Expirados</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.expiredUsers}</span>
                    <span className="text-[9px] text-red-500 font-bold block mt-1">Requerem reativação</span>
                  </div>
                </Card>

                {/* 6. Waitlist signups */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-orange-600 font-extrabold uppercase tracking-wider">Waitlist</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.premiumWaitlist}</span>
                    <span className="text-[9px] text-orange-600 font-bold block mt-1">Lista de Espera Premium</span>
                  </div>
                </Card>

                {/* 7. Tests Completed */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                      <PlayCircle className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Simulados</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.testsCompleted}</span>
                    <span className="text-[9px] text-slate-400 font-medium block mt-1">Concluídos na plataforma</span>
                  </div>
                </Card>

                {/* 8. AI Corrections */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
                      <BarChart2 className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Correções</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.writingCorrections}</span>
                    <span className="text-[9px] text-teal-600 font-bold block mt-1">Redações EE corrigidas</span>
                  </div>
                </Card>

                {/* 9. Flashcards Created */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <Layers className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Flashcards</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.flashcardsTotal}</span>
                    <span className="text-[9px] text-slate-400 font-medium block mt-1">Personalizados em SRS</span>
                  </div>
                </Card>

                {/* 10. Errors Saved */}
                <Card className="border-none shadow-xs bg-white p-4.5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600">
                      <FileWarning className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Erros</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">{stats?.errorsTotal}</span>
                    <span className="text-[9px] text-slate-400 font-medium block mt-1">Desvios no Banco de Erros</span>
                  </div>
                </Card>

              </div>

              {/* STUDY TIME TRACKER ADMIN STATS & LEADERBOARD */}
              {studyStats && (
                <div className="space-y-6 mt-6">
                  <div className="border-b border-slate-100 pb-3 text-left">
                    <h3 className="text-lg font-black text-slate-800 tracking-tight">Estatísticas de Tempo de Estudo</h3>
                    <p className="text-slate-500 text-xs font-semibold mt-0.5">Visão geral do engajamento e tempo de estudo ativo dos alunos esta semana.</p>
                  </div>

                  <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                    {/* KPI 1: Tempo Total de Estudo esta Semana */}
                    <Card className="border-none shadow-xs bg-white p-5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                          <Clock className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Tempo Total de Estudo</span>
                      </div>
                      <div className="mt-4">
                        <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">
                          {(() => {
                            const hrs = Math.floor(studyStats.totalStudyTimeThisWeek / 3600);
                            const mins = Math.floor((studyStats.totalStudyTimeThisWeek % 3600) / 60);
                            return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                          })()}
                        </span>
                        <span className="text-[9px] text-slate-400 font-medium block mt-1">Tempo acumulado por todos os usuários esta semana</span>
                      </div>
                    </Card>

                    {/* KPI 2: Tempo Médio por Aluno */}
                    <Card className="border-none shadow-xs bg-white p-5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                          <Users className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Média por Aluno Ativo</span>
                      </div>
                      <div className="mt-4">
                        <span className="text-2xl font-black text-slate-950 block tracking-tight leading-none">
                          {(() => {
                            const hrs = Math.floor(studyStats.averageStudyTimePerUser / 3600);
                            const mins = Math.floor((studyStats.averageStudyTimePerUser % 3600) / 60);
                            return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                          })()}
                        </span>
                        <span className="text-[9px] text-slate-400 font-medium block mt-1">Tempo médio de estudo ativo por aluno</span>
                      </div>
                    </Card>

                    {/* KPI 3: Atividade Mais Estudada */}
                    <Card className="border-none shadow-xs bg-white p-5 rounded-2xl flex flex-col justify-between border border-slate-100/80 hover:shadow-sm transition-all text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                          <BookOpen className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Foco Mais Estudado</span>
                      </div>
                      <div className="mt-4">
                        <span className="text-lg font-black text-slate-950 block tracking-tight truncate max-w-[220px]" title={studyStats.mostStudiedActivity}>
                          {studyStats.mostStudiedActivity}
                        </span>
                        <span className="text-[9px] text-slate-400 font-medium block mt-1">Área com maior dedicação e tempo ativo</span>
                      </div>
                    </Card>
                  </div>

                  {/* Alunos Mais Ativos (Leaderboard Table) */}
                  <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between text-left">
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900">Alunos Mais Ativos por Tempo de Estudo</h4>
                        <p className="text-[10px] text-slate-400 font-bold leading-normal mt-0.5">Ranking dos alunos dedicando mais tempo aos estudos esta semana.</p>
                      </div>
                      <Badge className="bg-indigo-50 border-none text-indigo-700 font-extrabold text-[10px] px-2.5 py-1">
                        Esta Semana
                      </Badge>
                    </div>
                    <CardContent className="p-0">
                      {studyStats.activeLearners.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 italic text-xs font-semibold">
                          Nenhum registro de tempo de estudo ativo esta semana
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">
                                <th className="px-6 py-3.5">Aluno</th>
                                <th className="px-6 py-3.5">E-mail</th>
                                <th className="px-6 py-3.5 text-right">Tempo Ativo</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                              {studyStats.activeLearners.slice(0, 10).map((learner: any, idx: number) => (
                                <tr key={learner.userId} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-6 py-3.5 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-[9px]">{idx + 1}</span>
                                    <span className="font-extrabold text-slate-900">{learner.userName}</span>
                                  </td>
                                  <td className="px-6 py-3.5 font-mono text-slate-500 text-[11px]">{learner.userEmail}</td>
                                  <td className="px-6 py-3.5 text-right font-black text-slate-900">
                                    {(() => {
                                      const hrs = Math.floor(learner.totalStudyTime / 3600);
                                      const mins = Math.floor((learner.totalStudyTime % 3600) / 60);
                                      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                                    })()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Interactive Recharts Graphics Grid (2 Cards) */}

              <div className="grid gap-6 md:grid-cols-2">
                
                {/* 1. Plans & Growth breakdowns */}
                <Card className="border-none shadow-sm bg-white rounded-3xl p-6 border border-slate-100 flex flex-col justify-between min-h-[380px]">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      Planos de Contas & Interesse na Lista Premium
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Distribuição de usuários ativos versus signups interessados em migrar para Premium.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 flex flex-col md:flex-row gap-6 items-center justify-center pt-2">
                    
                    {/* Donut chart for users plan */}
                    <div className="w-full max-w-[200px] h-[180px]">
                      {planChartData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-slate-400 italic">Sem dados de usuários</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={planChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={70}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {planChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} usuários`, 'Contas']} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Donut legends */}
                    <div className="space-y-2 flex-1 w-full">
                      {planChartData.map((d, index) => (
                        <div key={index} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                            <span className="font-semibold text-slate-600">{d.name}</span>
                          </div>
                          <span className="font-black text-slate-800">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Competency completes distribution */}
                <Card className="border-none shadow-sm bg-white rounded-3xl p-6 border border-slate-100 flex flex-col justify-between min-h-[380px]">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <BarChart2 className="w-4 h-4 text-primary" />
                      Engajamento por Competência (CE, CO, EE)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Simulados concluídos divididos por compreensão e redações de expressão escrita corrigidas.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 flex items-center justify-center pt-2">
                    <div className="w-full h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={competencyChartData}
                          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 9, fontWeight: 700, fill: "#64748b" }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fontWeight: 700, fill: "#64748b" }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(value) => [`${value} concluídos`, 'Atividade']} />
                          <Bar dataKey="simulados" radius={[6, 6, 0, 0]}>
                            {competencyChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Area chart of interest over time and Trial Funnel Segment */}
              <div className="grid gap-6 md:grid-cols-2">
                
                {/* 1. Weekly premium interest (growth) */}
                <Card className="border-none shadow-sm bg-white rounded-3xl p-6 border border-slate-100 min-h-[360px] flex flex-col justify-between">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-orange-600" />
                      Crescimento da Lista de Espera (Últimas Semanas)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Gráfico cumulativo semanal representando o interesse na conta Premium.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 flex items-center justify-center">
                    <div className="w-full h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={weeklyWaitlistData}
                          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="colorWaitlist" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f97316" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="week" tick={{ fontSize: 9, fontWeight: 700, fill: "#64748b" }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fontWeight: 700, fill: "#64748b" }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(value) => [`${value} interessados`, 'Cumulativo']} />
                          <Area type="monotone" dataKey="waitlist" stroke="#f97316" strokeWidth={2.5} fillOpacity={1} fill="url(#colorWaitlist)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Trial Conversion Funnel Pipe */}
                <Card className="border-none shadow-sm bg-white rounded-3xl p-6 border border-slate-100 min-h-[360px] flex flex-col justify-between">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      Funil de Conversão do Produto
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Etapas da jornada e taxas de conversão de novos alunos em assinantes Premium.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 flex flex-col justify-center gap-3 pt-2">
                    {funnelStages.map((stage, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-xs font-bold">
                          <div className="flex items-center gap-2 text-slate-700">
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-200 flex items-center justify-center text-[7px] font-black">{idx + 1}</span>
                            <span>{stage.stage}</span>
                            <span className="text-[10px] text-slate-400 font-medium">({stage.label})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-950 font-black">{stage.count}</span>
                            <Badge className="bg-slate-100 border-none text-slate-700 font-extrabold text-[9px] py-0 px-1.5">{stage.percentage}%</Badge>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${stage.color}`} style={{ width: `${stage.percentage}%` }} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

              </div>

            </div>
          )}

          {/* TAB 2: GERENCIAMENTO DE USUÁRIOS (Tabela, Filtros e Exportação CSV) */}
          {activeTab === "users" && (
            <Card className="border-none shadow-sm bg-white rounded-3xl border border-slate-100 overflow-hidden p-6 space-y-4">
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-50 pb-4">
                <div>
                  <CardTitle className="text-base font-black text-slate-900">Gerenciamento e Controle de Limites</CardTitle>
                  <CardDescription className="text-xs">Filtre usuários, audite simulações, altere planos e exporte relatórios.</CardDescription>
                </div>
                <Button
                  onClick={handleExportCSV}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs gap-1.5 shadow-sm self-start sm:self-auto px-4 h-10"
                >
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </Button>
              </div>

              {/* Filters Box */}
              <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between pt-1">
                
                <div className="relative flex-1 min-w-[280px]">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                  <input 
                    type="text"
                    placeholder="Buscar usuário por e-mail ou nome..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full bg-slate-50 text-slate-800 text-xs font-semibold rounded-xl pl-10 pr-4 py-3 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white transition-all"
                  />
                </div>

                <div className="flex flex-wrap gap-2.5 shrink-0">
                  {/* Plan filter */}
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <select 
                      value={planFilter}
                      onChange={e => setPlanFilter(e.target.value)}
                      className="bg-transparent focus:outline-none font-bold text-slate-700 cursor-pointer"
                    >
                      <option value="all">Todos os Planos</option>
                      <option value="free">Gratuito</option>
                      <option value="trial">Avaliação (Trial)</option>
                      <option value="premium">Premium</option>
                      <option value="expired">Acesso Expirado</option>
                    </select>
                  </div>

                  {/* Status filter */}
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <select 
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}
                      className="bg-transparent focus:outline-none font-bold text-slate-700 cursor-pointer"
                    >
                      <option value="all">Todos os Status</option>
                      <option value="active">Ativo</option>
                      <option value="inactive">Bloqueado / Suspenso</option>
                    </select>
                  </div>
                </div>

              </div>

              {/* Datagrid Table */}
              <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider">
                      <th className="p-4">Usuário / Cadastro</th>
                      <th className="p-4">Plano</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-center">Testes Usados</th>
                      <th className="p-4">Criado em</th>
                      <th className="p-4 text-right pr-6">Controles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-semibold">
                    {filteredProfiles.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400 italic">
                          Nenhum usuário correspondente aos filtros foi localizado na base.
                        </td>
                      </tr>
                    ) : (
                      filteredProfiles.map((p) => {
                        const signupDate = new Date(p.created_at).toLocaleDateString("pt-BR");
                        
                        let planBadge = "bg-slate-100 text-slate-700 border-none";
                        if (p.plan === "premium" || p.role === "admin") planBadge = "bg-emerald-100 text-emerald-800 border-none";
                        else if (p.plan === "trial") planBadge = "bg-amber-100 text-amber-800 border-none";
                        else if (p.plan === "expired") planBadge = "bg-red-100 text-red-800 border-none";

                        return (
                          <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-4">
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800">{p.name || p.email.split("@")[0]}</span>
                                <span className="text-[10px] text-slate-400">{p.email}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge className={`text-[10px] font-black py-0.5 px-2 capitalize ${planBadge}`}>
                                {p.role === "admin" ? `Admin (${p.plan})` : p.plan}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {p.is_active ? (
                                <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-200 text-[9px] font-extrabold py-0.5 px-2">
                                  Ativo
                                </Badge>
                              ) : (
                                <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 border-rose-200 text-[9px] font-extrabold py-0.5 px-2">
                                  Bloqueado
                                </Badge>
                              )}
                            </td>
                            <td className="p-4 text-center font-bold text-slate-700">
                              {p.trial_tests_used} / {p.trial_tests_limit}
                            </td>
                            <td className="p-4 text-slate-500 font-medium">
                              {signupDate}
                            </td>
                            <td className="p-4 text-right pr-6">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewUserDetail(p)}
                                className="text-primary hover:bg-primary/5 rounded-lg px-3 py-1 font-bold text-xs gap-1"
                              >
                                Detalhes e RLS
                                <ChevronRight className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

            </Card>
          )}

          {/* TAB 3: LOGS DE ATIVIDADES RECENTES (Histórico Cronológico) */}
          {activeTab === "logs" && (
            <Card className="border-none shadow-sm bg-white rounded-3xl border border-slate-100 p-6 space-y-4">
              <div>
                <CardTitle className="text-base font-black text-slate-900">Histórico de Ações da Plataforma</CardTitle>
                <CardDescription className="text-xs">
                  Eventos recentes de telemetria emitidos dinamicamente por usuários da plataforma em tempo real.
                </CardDescription>
              </div>

              {activityEvents.length === 0 ? (
                <div className="py-16 text-center text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold">Nenhum evento registrado recentemente.</p>
                  <p className="text-xs text-slate-400 mt-1">Interações e logs de atividade aparecerão conforme os usuários utilizarem o TEF Coach.</p>
                </div>
              ) : (
                <div className="border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden bg-white max-h-[600px] overflow-y-auto">
                  {activityEvents.map((evt) => {
                    const eventDate = new Date(evt.created_at).toLocaleString("pt-BR");
                    return (
                      <div key={evt.id} className="p-4 hover:bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors">
                        <div className="flex items-start gap-3">
                          <Badge className={`text-[9px] font-black border uppercase shrink-0 py-0.5 px-2 ${getEventBadgeStyles(evt.event_type)}`}>
                            {getEventNameLabel(evt.event_type)}
                          </Badge>
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-slate-800">{evt.email}</p>
                            {evt.metadata && Object.keys(evt.metadata).length > 0 && (
                              <p className="text-[10px] text-slate-400">
                                {evt.metadata.category ? `Deck: ${evt.metadata.category} | ` : ""}
                                {evt.metadata.front ? `Frente: "${evt.metadata.front}" | ` : ""}
                                {evt.metadata.severity ? `Gravidade: ${evt.metadata.severity} | ` : ""}
                                {evt.resource_type ? `Recurso: ${evt.resource_type}` : ""}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 shrink-0 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-slate-300" />
                          {eventDate}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

        </div>
      )}

      {/* User Details & Control Overlay Modal */}
      {selectedUser && (
        <ContextModal
          isOpen={userDetailsOpen}
          onClose={() => setUserDetailsOpen(false)}
          title={`${selectedUser.name || selectedUser.email.split("@")[0]}`}
          description={t("admin.modalAuditsDescription", "Consolidação qualitativa e controle administrativo de acessos.")}
          className="max-w-2xl"
        >
          {userDetailsLoading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
              <p className="text-xs text-slate-500">{t("admin.loadingAudits", "Buscando histórico estrutural no Supabase...")}</p>
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Detailed Metrics Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div className="space-y-0.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Simulados</span>
                  <span className="text-sm font-black text-slate-800 block">
                    {userTests.length} concluintes
                  </span>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Flashcards</span>
                  <span className="text-sm font-black text-slate-800 block">
                    {userFlashcardsCount} cards criados
                  </span>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Banco de Erros</span>
                  <span className="text-sm font-black text-slate-800 block">
                    {userErrorsCount} desvios
                  </span>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Cadastro</span>
                  <span className="text-xs font-bold text-slate-600 block pt-0.5">
                    {new Date(selectedUser.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>

              {/* Administrative Control Panel */}
              <div className="space-y-4 border-t border-slate-100 pt-4">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  Configurações de Assinatura, Planos e Limites
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Edit Plan Dropdown */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-600 block">Plano da Conta</label>
                    <select
                      value={editPlan}
                      onChange={e => setEditPlan(e.target.value as any)}
                      className="w-full bg-slate-50 text-slate-800 font-bold text-sm rounded-xl px-3 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white cursor-pointer"
                    >
                      <option value="free">Gratuito (Free)</option>
                      <option value="trial">Avaliação (Trial)</option>
                      <option value="premium">Assinante Premium</option>
                      <option value="expired">Acesso Expirado</option>
                    </select>
                  </div>

                  {/* Edit Limit Number */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-600 block">Limite de Simulados (Free/Trial)</label>
                    <input 
                      type="number"
                      min={1}
                      max={100}
                      value={editLimit}
                      onChange={e => setEditLimit(Number(e.target.value))}
                      className="w-full bg-slate-50 text-slate-800 font-bold text-sm rounded-xl px-3 py-2 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Reset test usages override */}
                <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100 text-xs">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Simulados Usados: <span className="font-black text-primary">{selectedUser.trial_tests_used} / {selectedUser.trial_tests_limit}</span></span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      Redefina o contador de testes realizados por esta conta gratuitamente.
                    </span>
                  </div>
                  <Button
                    type="button"
                    onClick={handleResetTestsUsage}
                    disabled={isResettingUsage}
                    variant="outline"
                    className="rounded-xl border-slate-200 font-bold h-9 gap-1 text-xs hover:bg-slate-100"
                  >
                    {isResettingUsage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 text-slate-400" />}
                    Zerar Testes Realizados
                  </Button>
                </div>

                {/* Active / Suspend Status */}
                <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100 text-xs">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Bloquear ou Suspender Acesso</span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {editIsActive ? "O usuário pode fazer login e utilizar os serviços normalmente." : "O usuário terá o login bloqueado e não conseguirá acessar."}
                    </span>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setEditIsActive(!editIsActive)}
                    variant={editIsActive ? "outline" : "destructive"}
                    className="rounded-xl font-bold h-9 gap-1 text-xs"
                  >
                    {editIsActive ? (
                      <>
                        <Lock className="w-3.5 h-3.5 text-rose-500" />
                        Suspender Conta
                      </>
                    ) : (
                      <>
                        <Unlock className="w-3.5 h-3.5" />
                        Reativar Conta
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Audits History Logs lists */}
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-slate-400" />
                  Simulados Concluídos Recentemente
                </h3>
                
                {userTests.length === 0 ? (
                  <p className="text-xs text-slate-400 italic bg-slate-50 p-4 rounded-xl border border-slate-100 text-center">
                    Nenhum simulado concluído para este aluno ainda.
                  </p>
                ) : (
                  <div className="max-h-[180px] overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
                    {userTests.slice(0, 5).map((test, idx) => {
                      const isWriting = test.competence === "Writing" || test.competence === "Expression Écrite (EE)";
                      
                      return (
                        <div key={idx} className="p-3 bg-white hover:bg-slate-50/30 flex items-center justify-between text-xs transition-colors">
                          <div className="space-y-0.5">
                            <span className="font-bold text-slate-800">{getAttemptCompetenceLabel(test)}</span>
                            <span className="text-[10px] text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-300" />
                              {new Date(test.date).toLocaleString("pt-BR")}
                            </span>
                          </div>
                          <Badge className="bg-slate-100 text-slate-700 font-bold border-none text-[10px]">
                            {isWriting ? `${test.words || 0} palavras` : test.score || "0"}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal controls */}
              <div className="pt-4 border-t border-slate-100 flex gap-2 justify-end">
                <Button 
                  variant="outline"
                  onClick={() => setUserDetailsOpen(false)}
                  disabled={isSavingUser}
                  className="rounded-xl border-slate-200 font-bold text-xs"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveUserOverrides}
                  disabled={isSavingUser}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-sm flex items-center gap-1.5"
                >
                  {isSavingUser && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Salvar Overrides
                </Button>
              </div>
            </div>
          )}
        </ContextModal>
      )}

      {/* Floating success toast notifications */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-slate-900 text-white font-bold text-xs py-3.5 px-6 rounded-2xl shadow-xl flex items-center gap-2 animate-in slide-in-from-top duration-300">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}
      
    </div>
  );
}
